import { FC } from 'react';
import { useNavigate } from 'react-router';
import {
  Card,
  TextSubtitle,
  Spacer,
  Stack,
  Tag,
  TextBody,
  TextButton,
  Avatar,
  Button,
  Icon,
} from '@pypestream/design-system';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

import {
  capitalizeWords,
  formatDate,
  readDeploymentPhase,
  readDraftLabelSuffix,
  readDeploymentStep,
  type DeploymentProgressRow,
} from '../../utils';
import { SolutionVersion } from '../../xstate/prostudio.api-service.types';
import { useProStudioRoutes } from '../../hooks';

export interface VersionCardProps {
  solution: SolutionVersion;
  isViewer: boolean;
  /**
   * This environment's most recent publish attempt — running or failed included, which is
   * the point: `solution` only ever describes the last SUCCESSFUL publish, so without this
   * a publish that is still running, or one that failed, is invisible here.
   */
  deployment?: DeploymentProgressRow;
  /** Who started that attempt; the row itself carries only their user id. */
  publisherName?: string;
  onViewLog?: (deployment: DeploymentProgressRow) => void;
}

export const VersionCard: FC<VersionCardProps> = ({
  solution,
  isViewer,
  deployment,
  publisherName,
  onViewLog,
}) => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { workflows } = useProStudioRoutes();
  const testId = `ps-versions-page-${solution.env.toLowerCase()}-card`;
  const { version, env, time, first_name, last_name, picture } = solution;
  const fullName = `${first_name} ${last_name}`;

  // A finished deployment says nothing the card doesn't already show through `solution`;
  // only a running or failed attempt earns a line of its own.
  const phase = deployment ? readDeploymentPhase(deployment.status) : undefined;
  const step = deployment ? readDeploymentStep(deployment.status) : undefined;
  const isFailed = phase === 'failed';
  const publishStatusLabel = isFailed
    ? step
      ? t('pro-studio/versions:cards.publish_status.failed_step', {
          defaultValue: 'Publish failed · {{step}}',
          step,
        })
      : t('pro-studio/versions:cards.publish_status.failed', {
          defaultValue: 'Publish failed',
        })
    : step
      ? t('pro-studio/versions:cards.publish_status.publishing_step', {
          defaultValue: 'Publishing · {{step}}',
          step,
        })
      : t('pro-studio/versions:cards.publish_status.publishing', {
          defaultValue: 'Publishing…',
        });

  const publishStatus =
    deployment && phase && phase !== 'succeeded' ? (
      <>
        <Spacer size="small" />
        <Stack gutter="xsmall" alignItems="center">
          <Tag
            test-id={`${testId}-publish-status`}
            minimal
            size="small"
            variant={isFailed ? 'error' : 'warning'}
          >
            {publishStatusLabel}
          </Tag>
          {publisherName && (
            <TextBody size="small" variant="secondary">
              {t('pro-studio/versions:cards.publish_status.by', {
                defaultValue: 'by {{name}}',
                name: publisherName,
              })}
            </TextBody>
          )}
          {isFailed && onViewLog && (
            <TextButton
              test-id={`${testId}-publish-status-log`}
              size="small"
              onClick={() => onViewLog(deployment)}
            >
              {t('pro-studio/versions:cards.publish_status.view_log', {
                defaultValue: 'View log',
              })}
            </TextButton>
          )}
        </Stack>
      </>
    ) : null;

  if (!solution.version && solution.env === 'draft') {
    return (
      <Card size="xlarge" border="default" shadow="none" test-id={testId}>
        <TextSubtitle>
          <TranslationComponent i18nKey="pro-studio/common:versions.draft">
            Draft
          </TranslationComponent>{' '}
          <span color="primary">v-</span>
        </TextSubtitle>
        <Spacer size="small" />
        <TextBody size="small" variant="secondary">
          <TranslationComponent i18nKey="pro-studio/versions:cards.empty">
            To create a new draft, make an edit in the Workflows tab or load an
            older version.
          </TranslationComponent>
        </TextBody>
      </Card>
    );
  }

  return (
    <Card
      test-id={testId}
      size="xlarge"
      border="primary"
      shadow="medium"
      variant="glass"
    >
      {env === 'draft' ? (
        <>
          <Stack
            gutter="small"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack gutter="2xsmall" alignItems="baseline">
              <TextSubtitle>
                <TranslationComponent i18nKey="pro-studio/common:versions.draft">
                  Draft
                </TranslationComponent>{' '}
                <span color="primary">{readDraftLabelSuffix(version)}</span>
              </TextSubtitle>
              {/* <TextBody size="small" variant="tertiary">
              (
              <TranslationComponent i18nKey="pro-studio/common:versionPrefix">
                from
              </TranslationComponent>{' '}
              v5)
            </TextBody> */}
            </Stack>
            {!isViewer && (
              <Button
                test-id="ps-versions-page-edit-workflows-btn"
                variant="secondary"
                size="small"
                disabled={!version}
                onClick={() => {
                  navigate(workflows.path);
                }}
              >
                <Icon name="pencil" slot="prefix" size="xsmall" />
                <TranslationComponent i18nKey="pro-studio/versions:cards.button">
                  Edit Workflows
                </TranslationComponent>
              </Button>
            )}
          </Stack>
        </>
      ) : (
        <TextSubtitle>
          <TranslationComponent
            i18nKey={`pro-studio/common:versions.${env as 'draft' | 'sandbox' | 'live'}`}
          >
            {env}
          </TranslationComponent>{' '}
          <span color="primary">{version || 'v-'}</span>
        </TextSubtitle>
      )}

      {fullName && time && version ? (
        <>
          <Spacer size="small" />
          <Stack gutter="2xsmall">
            <TextBody size="small">
              {time ? formatDate(time) : ''}{' '}
              <TranslationComponent i18nKey="pro-studio/common:userNamePrefix">
                by
              </TranslationComponent>{' '}
            </TextBody>
            <Stack gutter="2xsmall" nowrap>
              <Avatar size="small" label={fullName} src={picture} />
              <TextBody size="small">{fullName}</TextBody>
            </Stack>
          </Stack>
        </>
      ) : env !== 'draft' ? (
        <>
          <Spacer size="small" />
          <Stack gutter="2xsmall">
            <TextBody size="small">
              {t('pro-studio/versions:cards.no_published_label', {
                env: capitalizeWords(env),
              })}
            </TextBody>
          </Stack>
        </>
      ) : null}
      {publishStatus}
    </Card>
  );
};
